// Email a statement to one patient. The second step of a two-step send: the row's
// "Send statement" button opens this, and only the confirm button here sends.
//
// What the confirm restates, from the server's preview: the exact address on the
// patient's record (there is no field to type another), the amount and the visits.
// Statements carry no pay link (2026-10-19). The send posts that address back as `expected_email`;
// if the record changed in between, the server refuses (409) and this dialog shows
// the new address for a fresh confirm — it never re-sends on its own.
//
// One click, one request: the confirm is disabled while the request is in flight, a
// ref guards against a second click landing before the re-render, and once a send
// has produced an answer that may have delivered (sent, failed, timed out, "sent
// moments ago") the confirm stays disabled until the dialog is closed.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatPatientName } from "../../utils/formatName";
import {
  centsToDollars,
  fetchStatementPreview,
  formatDay,
  isAmbiguousSendError,
  formatWhen,
  sendStatement,
  sendStatusChip,
} from "./api";
import "./Collections.css";

export default function SendStatementModal({
  practiceId,
  patientId,
  onClose,
  onSent,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Set once a send attempt may have delivered; the confirm never re-enables.
  const [settled, setSettled] = useState(false);
  const [message, setMessage] = useState(null); // {type, text}
  const inFlight = useRef(false);
  // Whether a POST went out from this dialog, so closing it knows to refresh the
  // list behind it (a failed send still writes a row) and a Cancel does not.
  const attempted = useRef(false);
  const closeRef = useRef(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const body = await fetchStatementPreview(practiceId, patientId);
      setPreview(body);
      return body;
    } catch (err) {
      setPreview(null);
      setMessage({ type: "error", text: err.message });
      return null;
    } finally {
      setLoading(false);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !inFlight.current) onClose(attempted.current);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canConfirm =
    !!preview &&
    preview.canSend &&
    !!preview.toEmail &&
    !loading &&
    !busy &&
    !settled;

  const confirm = async () => {
    if (!canConfirm || inFlight.current) return;
    inFlight.current = true;
    attempted.current = true;
    setBusy(true);
    setMessage(null);
    const expected = preview.toEmail;
    try {
      const send = await sendStatement(practiceId, patientId, expected);
      setSettled(true);
      onSent(send, expected);
    } catch (err) {
      const changedAddress =
        err.status === 409 && err.body && typeof err.body.to_email === "string";
      if (changedAddress) {
        // Nothing was sent. Show the address now on file and ask again.
        const fresh = await loadPreview();
        setMessage({
          type: "warn",
          text: fresh
            ? "The patient's email address changed since this dialog opened. Check the address below and confirm again."
            : err.message,
        });
      } else if ([400, 403, 404, 429, 503].includes(err.status)) {
        // Refused before anything was written (bad request, no write access or a
        // CSRF refusal, rate limit, not configured); nothing can have gone out.
        setMessage({ type: "error", text: err.message });
      } else {
        // 409 "sent moments ago", 422, 5xx, or no answer at all: any of these may
        // mean an email exists. No retry from this dialog.
        setSettled(true);
        const failedRow = err.body && err.body.send;
        const definitelyNotSent =
          err.status === 409 ||
          err.status === 422 ||
          (err.status === 502 &&
            failedRow &&
            failedRow.error &&
            !isAmbiguousSendError(failedRow.error));
        setMessage({
          type: "error",
          text: definitelyNotSent
            ? err.message
            : "No clear answer from the mail service, so this statement may or may not have gone out. Close this and check the statements emailed before trying again.",
        });
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const name = preview
    ? formatPatientName(preview.patientName)
    : "this patient";

  return (
    <div
      className="coll-modal-backdrop"
      onClick={() => !inFlight.current && onClose(attempted.current)}
    >
      <div
        className="coll-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coll-send-title"
      >
        <div className="coll-modal-header">
          <h2 id="coll-send-title">Email a statement</h2>
          <button
            ref={closeRef}
            className="coll-modal-close"
            onClick={() => onClose(attempted.current)}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="coll-modal-body">
          {loading && !preview && <p className="coll-muted">Loading…</p>}

          {preview && (
            <>
              <p className="coll-modal-lede">
                This emails {name} their statement as a PDF. It goes to the
                address on their record — to use a
                different address, change the patient's record first.
              </p>

              <dl className="coll-confirm-facts">
                <dt>To</dt>
                <dd className="coll-confirm-email" data-testid="send-to-email">
                  {preview.toEmail || "No email address on file"}
                </dd>
                <dt>Amount due</dt>
                <dd>{formatCurrency(centsToDollars(preview.balanceCents))}</dd>
              </dl>

              {preview.lines.length > 0 && (
                <table className="coll-mini-table">
                  <thead>
                    <tr>
                      <th>Date of service</th>
                      <th className="num">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.lines.map((line, i) => (
                      <tr key={`${line.dos}-${i}`}>
                        <td>{formatDay(line.dos)}</td>
                        <td className="num">
                          {formatCurrency(centsToDollars(line.balanceCents))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {preview.lastSend && (
                <p className="coll-muted">
                  Last statement: {formatWhen(preview.lastSend.sentAt)} to{" "}
                  {preview.lastSend.toEmail || "—"} —{" "}
                  {sendStatusChip(preview.lastSend.status, preview.lastSend.error).label.toLowerCase()}.
                </p>
              )}

              {!preview.canSend && (
                <div className="coll-message error" role="alert">
                  {preview.reason || "This statement cannot be sent."}
                </div>
              )}
            </>
          )}

          {message && (
            <div className={`coll-message ${message.type}`} role="alert">
              {message.text}
            </div>
          )}
        </div>

        <div className="coll-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onClose(attempted.current)}
            disabled={busy}
          >
            {settled ? "Close" : "Cancel"}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={confirm}
            disabled={!canConfirm}
          >
            {busy
              ? "Sending…"
              : preview && preview.toEmail
                ? `Send to ${preview.toEmail}`
                : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
